
export interface HotCacheEntry<K, V> {
  used: boolean;
  key: K;
  value: V;
  lastRead: number;
}

// Quick lookup cache using a free list and index stack
// Entries are preallocated and reused
// freeIndices is the indices to entries that can be allocated
// usedIndices is an ordered list of indices currently allocated
// usedCount is the number of items in usedIndices
// allocations are avoided in all operations
export class HotCache<K, V> {
  private entries: HotCacheEntry<K, V>[];
  private freeIndices: number[];
  private usedIndices: number[];
  private usedCount = 0;

  constructor(public readonly size: number) {
    this.entries = [];
    this.freeIndices = [];
    this.usedIndices = [];

    for (let i = 0; i < size; ++i) {
      this.entries.push({ used: false, key: null, value: null, lastRead: 0 });
      this.freeIndices.push(size - i - 1);
      this.usedIndices.push(-1);
    }
  }

  public getOrAdd(key: K, factory: (key: K) => V) {
    let value = this.tryGet(key);
    if (value === undefined) {
      value = factory(key);
      this.add(key, value);
    }

    return value;
  }

  public tryGet(key: K) {
    let usedCount = this.usedCount;
    if (usedCount === 0)
      return;

    let currentTime = Date.now();

    for (let i = 0; i < usedCount; ++i) {
      let entry = this.entries[this.usedIndices[i]];
      if (entry.key === key) {
        entry.lastRead = currentTime;
        return entry.value;
      }
    }

    return undefined;
  }

  public add(key: K, value: V) {
    let currentTime = Date.now();

    let index = 0;

    if (this.freeIndices.length) {
      index = this.freeIndices.pop();
      this.usedIndices[this.usedCount++] = index;
    }
    else {
      let maxLastRead = currentTime;

      // Select oldest index
      let usedCount = this.usedCount;
      for (let i = 0; i < usedCount; ++i) {
        let entry = this.entries[this.usedIndices[i]];
        let lastRead = entry.lastRead;
        if (lastRead < maxLastRead) {
          index = i;
          maxLastRead = lastRead;
        }
      }

      // Swap last index with the current index to remove the last 'used' item
      let lastIndex = this.usedCount - 1;
      this.usedIndices[index] = this.usedIndices[lastIndex];
    }

    let entry = this.entries[index];

    entry.key = key;
    entry.value = value;
    entry.lastRead = currentTime;
    entry.used = true;
  }

  public tryPrune(isExpired: (value: V, key: K) => boolean) {
    let usedCount = this.usedCount;
    if (usedCount === 0)
      return;

    let pruned = false;

    let newUsedCount = 0;

    for (let i = 0; i < usedCount; ++i) {
      let index = this.usedIndices[i];
      let entry = this.entries[index];
      if (entry.used) {
        if (isExpired(entry.value, entry.key)) {
          entry.key = null;
          entry.value = null;
          entry.lastRead = 0;
          this.freeIndices.push(i);
          pruned = true;
        }
        else {
          this.usedIndices[newUsedCount++] = index;
        }
      }
    }

    this.usedCount = newUsedCount;

    return pruned;
  }
}
